import { callLambdaFunction } from './aiClient';

const CHAT_COMPLETION_ENDPOINT = import.meta.env?.VITE_AWS_LAMBDA_CHAT_COMPLETION_URL;
const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL;

/**
 * Send quote confirmation email via Supabase Edge Function
 */
async function sendQuoteConfirmationEmail(email, companyName, contactName, quote, clientInfo) {
  try {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/send-professional-quote-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email,
        companyName,
        contactName,
        quote,
        clientInfo
      })
    });

    if (!response?.ok) {
      const errorData = await response?.json();
      throw new Error(errorData?.error || 'Failed to send confirmation email');
    }

    return await response?.json();
  } catch (error) {
    console.error('Quote confirmation email error:', error);
    throw error;
  }
}

/**
 * Schedule follow-up reminder email (48 hours after quote)
 */
async function scheduleFollowUpEmail(email, companyName, contactName, quoteDate) {
  try {
    // Schedule follow-up for 48 hours later
    const followUpDelay = 48 * 60 * 60 * 1000; // 48 hours in milliseconds
    
    setTimeout(async () => {
      try {
        const response = await fetch(`${SUPABASE_URL}/functions/v1/send-professional-followup-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email,
            companyName,
            contactName,
            quoteDate
          })
        });

        if (!response?.ok) {
          console.error('Follow-up email scheduling failed');
        }
      } catch (error) {
        console.error('Follow-up email error:', error);
      }
    }, followUpDelay);

    return { scheduled: true, delay: '48 hours' };
  } catch (error) {
    console.error('Follow-up scheduling error:', error);
    return { scheduled: false, error: error?.message };
  }
}

/**
 * Generate personalized shipping quote using OpenAI
 * Analyzes client requirements and calculates costs based on volume, type, and destination
 * 
 * @param {Object} formData - Professional inquiry form data
 * @returns {Promise<Object>} Generated quote with pricing and recommendations
 */
export async function generateProfessionalQuote(formData) {
  const systemPrompt = `Tu es un expert en logistique maritime et transport international pour ADT Express, spécialisé dans les envois France-Côte d'Ivoire. Ton rôle est d'analyser les besoins des clients professionnels et de générer des devis personnalisés précis.

**TARIFS DE RÉFÉRENCE (2026):**

**BARRIQUES:**
- Barrique noire 270L: 150€ TTC (Offre Été 2026 valable jusqu'en septembre)
- Barrique bleue 220L: 140€ TTC
- Tarif dégressif dès 5 barriques: -10%
- Tarif dégressif dès 10 barriques: -15%
- Tarif dégressif dès 20 barriques: -20%

**ENVOI AU VOLUME (m³):**
- Prix de base: 180€/m³
- Volume 1-5 m³: 180€/m³
- Volume 6-15 m³: 160€/m³
- Volume 16-30 m³: 140€/m³
- Volume 30+ m³: 120€/m³

**CONTENEURS COMPLETS:**
- Conteneur 20 pieds (33 m³): 4500€ TTC
- Conteneur 40 pieds (67 m³): 7500€ TTC

**SERVICES ADDITIONNELS:**
- Certificat EX1 (récupération TVA): 80€
- Enlèvement hors Île-de-France: 150-400€ selon distance
- Livraison à domicile Abidjan: 50-150€ selon zone
- Assurance marchandise: 2% de la valeur déclarée

**DÉLAIS:**
- Départ: 2 fois par mois (1er et 15 de chaque mois)
- Transit maritime: 25-30 jours
- Dédouanement: 3-5 jours ouvrés

Tu dois:
1. Analyser le type de client et ses besoins spécifiques
2. Recommander la solution la plus adaptée (barriques, volume, ou conteneur)
3. Calculer le coût total avec tous les frais
4. Expliquer les avantages de la solution recommandée
5. Mentionner le certificat EX1 si pertinent pour récupérer la TVA
6. Donner des conseils pratiques pour optimiser les coûts

Réponds en français, de manière professionnelle mais chaleureuse.`;

  const userPrompt = `Génère un devis personnalisé pour ce client professionnel:

**INFORMATIONS CLIENT:**
- Entreprise: ${formData?.companyName}
- Contact: ${formData?.contactName}
- Type de client: ${formData?.clientType}
- Type d'envoi souhaité: ${formData?.shippingType}
- Besoin certificat EX1: ${formData?.needsEX1}
- Adresse d'enlèvement: ${formData?.pickupAddress}
- Fréquence d'envoi: ${formData?.frequency}
- Détails supplémentaires: ${formData?.message || 'Non spécifié'}

Génère un devis détaillé avec:
1. Solution recommandée (barriques/volume/conteneur)
2. Calcul du coût total détaillé
3. Avantages de cette solution
4. Informations sur le certificat EX1 si applicable
5. Conseils pour optimiser les coûts
6. Prochaines étapes`;

  const payload = {
    provider: 'OPEN_AI',
    model: 'gpt-5',
    messages: [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt }
    ],
    stream: false,
    parameters: {
      max_completion_tokens: 2000,
      temperature: 1
    }
  };

  try {
    const response = await callLambdaFunction(CHAT_COMPLETION_ENDPOINT, payload);
    
    // Extract content from OpenAI response
    const quoteContent = response?.choices?.[0]?.message?.content || '';
    
    const result = {
      success: true,
      quote: quoteContent,
      timestamp: new Date()?.toISOString(),
      clientInfo: {
        company: formData?.companyName,
        contact: formData?.contactName,
        email: formData?.email
      }
    };

    // Send quote confirmation email automatically
    try {
      await sendQuoteConfirmationEmail(
        formData?.email,
        formData?.companyName,
        formData?.contactName,
        quoteContent,
        result?.clientInfo
      );
      
      result.emailSent = true;
      
      // Schedule follow-up reminder for 48 hours later
      await scheduleFollowUpEmail(
        formData?.email,
        formData?.companyName,
        formData?.contactName,
        result?.timestamp
      );
      
      result.followUpScheduled = true;
    } catch (emailError) {
      console.error('Email sending failed:', emailError);
      result.emailSent = false;
      result.emailError = emailError?.message;
    }
    
    return result;
  } catch (error) {
    console.error('Professional quote generation error:', error);
    throw new Error('Erreur lors de la génération du devis. Veuillez réessayer.');
  }
}

/**
 * Estimate volume based on shipping type and client description
 * Helper function for quick volume calculations
 */
export function estimateVolume(shippingType, message) {
  // Simple heuristic-based volume estimation
  const volumeMap = {
    'volume': 10, // Default 10 m³
    'barrels': 5,  // ~5 barriques average
    'container20': 33,
    'container40': 67,
    'unsure': 8
  };
  
  return volumeMap?.[shippingType] || 10;
}

/**
 * Calculate base price estimate before AI generation
 * Provides quick pricing feedback
 */
export function calculateBaseEstimate(shippingType, volume = 10) {
  switch (shippingType) {
    case 'container20':
      return { price: 4500, unit: 'conteneur' };
    case 'container40':
      return { price: 7500, unit: 'conteneur' };
    case 'barrels':
      return { price: 150 * volume, unit: `${volume} barriques` };
    case 'volume':
      const pricePerM3 = volume <= 5 ? 180 : volume <= 15 ? 160 : volume <= 30 ? 140 : 120;
      return { price: pricePerM3 * volume, unit: `${volume} m³` };
    default:
      return { price: 1800, unit: 'estimation' };
  }
}

export default {
  generateProfessionalQuote,
  estimateVolume,
  calculateBaseEstimate,
  sendQuoteConfirmationEmail,
  scheduleFollowUpEmail
};
function professionalQuoteService(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: professionalQuoteService is not implemented yet.', args);
  return null;
}

export { professionalQuoteService };